// Core packages
import {
  Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChildren
} from "@angular/core";
import { AbstractControl, FormControl, ValidationErrors } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { Observable, Subscription } from "rxjs";
import { finalize, take } from "rxjs/operators";

// Custom packages
import { DocumentArchivesService } from "src/app/modules/properties/document-archives.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ApiResponse } from "src/app/shared/interfaces/_base/api-response.interface";
import { DocumentArchiveNameLists } from "src/app/shared/interfaces/document-archive-doc.interface";

/**
 * Script start
 */

/** Max length accepted by the back-end for a conductor name. */
export const CONDUCTOR_NAME_MAX_LENGTH = 200;

/** Above this amount of rows the local search field is rendered. */
const CONDUCTOR_SEARCH_THRESHOLD = 10;

/** Sentinel used when the document count could not be derived. */
const DOCUMENT_COUNT_UNKNOWN = -1;

/** Root i18n key of every label used by this dialog. */
const I18N_ROOT = "properties.documentsPage.conductorManagement.";

/** Data contract expected by the dialog. */
export interface ConductorManagementDialogData {
  /** ID of the document archive (the same used for `flat-archive`). */
  documentArchiveId: string;
  /** Property name, rendered as dialog subtitle. */
  propertyName?: string;
  /** `conductorName` exactly as it comes from the back-end. */
  conductors: string[];
  /**
   * Document count per conductor name. A missing name means "unknown".
   * It is a LOWER BOUND: the archive tree only holds non-deleted documents
   * while the back-end counts deleted ones too.
   */
  documentCounts?: any;
  /**
   * Optional reader of the archive name lists, used by the reload after a
   * failed operation INSTEAD of the legacy `GET document-archives/:id`.
   *
   * The 3.0.0 dossier hands over one that reads
   * `GET document-archive-docs/name-lists` (EXT-20). Contract: it answers with
   * `data.conductorName` as the legacy route does, and it notifies the user of
   * its own errors before rethrowing them, as DocumentArchivesService.get does.
   * Left out, as the legacy dossier does, the dialog re-reads the legacy
   * archive exactly as before.
   */
  reloadNames?: () => Observable<ApiResponse<DocumentArchiveNameLists>>;
}

/** A single row of the managed list. */
export interface ConductorRow {
  /** Value as received from the back-end: the ONLY value allowed in payloads. */
  raw: string;
  /** Trimmed value (or the "(senza nome)" placeholder), for display only. */
  display: string;
  /** True when `raw` is empty or whitespace-only (historical bad data). */
  blank: boolean;
  /** Derived document count, or DOCUMENT_COUNT_UNKNOWN. */
  count: number;
  /** Inline error message shown under the row. */
  error: string;
}

/**
 * Manages the archive-wide list of "conduttori" (`conductorName` on the
 * archive, `data.nomeConduttore` on each document).
 *
 * It is the twin of InterventionArchivingDialogComponent and must keep behaving
 * the same way: same layout, same inline rename/delete, same ESC handling. The
 * two are kept separate because the contracts differ (the "unchanged" rule is
 * case-insensitive here, the error map carries 401 and `conductor_not_found`,
 * delete is idempotent), and merging them would mean rewriting a dialog already
 * in use. If a third one shows up, extract a shared base first.
 *
 * VISIBILITY: this dialog - and the entry point that opens it - must be
 * exposed only when the current role type is "admin" or "operator". Every
 * operation answers 403 for any other role, so showing the UI would be a dead
 * end. The check lives in the caller (see
 * PropertyDocumentsComponent.onManageConductors), which already has the role
 * type from the user context; the dialog stays agnostic so that the
 * authorization rule has a single home.
 */
@Component({
  selector: "app-conductor-management-dialog",
  templateUrl: "./conductor-management-dialog.component.html",
  styleUrls: ["./conductor-management-dialog.component.scss"],
})
export class ConductorManagementDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  @ViewChildren("renameTrigger") renameTriggers: QueryList<ElementRef>;
  @ViewChildren("renameInput") renameInputs: QueryList<ElementRef>;

  // Exposed to the template
  maxLength = CONDUCTOR_NAME_MAX_LENGTH;
  searchThreshold = CONDUCTOR_SEARCH_THRESHOLD;
  countUnknown = DOCUMENT_COUNT_UNKNOWN;

  documentArchiveId = "";
  propertyName = "";

  /** Raw names, exactly as persisted by the back-end. */
  conductors: string[] = [];
  rows: ConductorRow[] = [];
  displayedRows: ConductorRow[] = [];

  /** Local, client-side only filter. */
  searchTerm = "";

  /**
   * Owned copy of the document counts, keyed by intervention name.
   *
   * It must be owned and kept in sync by every operation: the map handed over
   * by the caller is a snapshot of the archive tree taken when the dialog
   * opened, so after a rename the new name would have no entry at all and the
   * row would fall back to "unknown", re-enabling a deletion the back-end
   * refuses. Null prototype because the keys are free text typed by the users.
   */
  private documentCounts: any = Object.create(null);

  // BEGIN - Create conductor form
  createControl: FormControl = new FormControl("");
  createServerError = "";
  creating = false;
  // END - Create conductor form

  /**
   * Row with an open inline action. Only one row at a time can be in
   * `editing` or `confirmingDelete`, so a single reference is enough.
   */
  activeRow: ConductorRow = null;
  /** "editing" | "confirmingDelete" | null */
  activeKind: string = null;
  draft = "";
  draftError = "";

  /** Row currently performing an API call (rename or delete). */
  savingRow: ConductorRow = null;

  /**
   * Blocking error on the archive itself (403, or 404 on create/delete):
   * retrying makes no sense, so every action gets disabled.
   */
  contextError = "";

  /** True as soon as an operation succeeded: the caller must refresh. */
  private changed = false;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<ConductorManagementDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConductorManagementDialogData,
    private documentArchivesService: DocumentArchivesService,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    public confirmDialog: MatDialog
  ) {
    // Every closing path goes through attemptClose(), so that a pending
    // rename draft can be confirmed before the dialog disappears.
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    if (this.data) {
      this.documentArchiveId = this.data.documentArchiveId
        ? this.data.documentArchiveId
        : "";
      this.propertyName = this.data.propertyName ? this.data.propertyName : "";
      // Copy the caller's snapshot before building the rows: from now on the
      // dialog owns it and never mutates the object it received
      this.copyDocumentCounts(this.data.documentCounts);
      this.setConductors(
        Array.isArray(this.data.conductors) ? this.data.conductors : []
      );
    }

    this.createControl.setValidators([this.nameValidator]);
    this.subscriptions.push(
      this.createControl.valueChanges.subscribe(() => {
        // A server-side rejection is stale as soon as the value changes
        this.createServerError = "";
        // Material only paints mat-error on a TOUCHED control: marking it here
        // surfaces the duplicate and length errors while typing, instead of
        // waiting for the blur or for the Crea click
        this.createControl.markAsTouched();
      })
    );

    // ESC must cancel the open row action first and close the dialog only
    // when every row is idle
    this.subscriptions.push(
      this.dialogRef.keydownEvents().subscribe((event: KeyboardEvent) => {
        if (event.key !== "Escape" && event.keyCode !== 27) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.onEscape();
      })
    );
    this.subscriptions.push(
      this.dialogRef.backdropClick().subscribe(() => this.attemptClose())
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Current raw value of the create field
   *
   * @since 1.0.0
   */
  get createValue(): string {
    return typeof this.createControl.value === "string"
      ? this.createControl.value
      : "";
  }

  /**
   * Normalized value of the create field: this is exactly what gets sent
   *
   * @since 1.0.0
   */
  get createNormalizedValue(): string {
    return this.normalizeName(this.createValue);
  }

  /**
   * Normalized value of the inline rename field: this is exactly what gets
   * sent as `to`
   *
   * @since 1.0.0
   */
  get draftNormalized(): string {
    return this.normalizeName(this.draft);
  }

  /**
   * True when no operation can be started (blocking context error or a
   * running API call)
   *
   * @since 1.0.0
   */
  get actionsDisabled(): boolean {
    return !!this.contextError || this.savingRow !== null || this.creating;
  }

  /**
   * True when the inline "Salva" button must be disabled
   *
   * @since 1.0.0
   */
  get renameDisabled(): boolean {
    if (!this.activeRow || this.savingRow !== null) {
      return true;
    }
    const to = this.draftNormalized;
    return !to || this.isUnchanged(to, this.activeRow.raw);
  }

  /**
   * Number of documents the pending rename is going to update, or
   * DOCUMENT_COUNT_UNKNOWN
   *
   * @since 1.0.0
   */
  get renameImpact(): number {
    return this.activeRow ? this.activeRow.count : DOCUMENT_COUNT_UNKNOWN;
  }

  /**
   * Handle the create field input
   *
   * @since 1.0.0
   */
  onCreateSubmit(): void {
    if (this.contextError || this.creating) {
      return;
    }

    this.createControl.markAsTouched();
    this.createControl.updateValueAndValidity();
    if (this.createControl.invalid) {
      return;
    }

    const name = this.createNormalizedValue;
    this.creating = true;
    this.createServerError = "";
    this.subscriptions.push(
      this.documentArchivesService
        .createConductor(this.documentArchiveId, name)
        .pipe(finalize(() => (this.creating = false)))
        .subscribe(
          (res) => {
            if (!res || !res.status) {
              this.createServerError = this.translateService.instant(
                I18N_ROOT + "errors.unexpected"
              );
              return;
            }
            // Always trust the persisted (trimmed) name over the sent one
            const persisted =
              res.data && typeof res.data.name === "string"
                ? res.data.name
                : name;
            this.addLocal(persisted);
            this.changed = true;
            this.createControl.setValue("");
            this.createControl.markAsUntouched();
            this.toastrService.success(
              this.resolveMessage(res, I18N_ROOT + "success.created")
            );
          },
          (err) => this.handleOperationError(err, "create", null)
        )
    );
  }

  /**
   * Apply the local search filter
   *
   * @since 1.0.0
   *
   * @param term New search term
   */
  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.applyFilter();
  }

  /**
   * Start the inline rename of the given row
   *
   * @since 1.0.0
   *
   * @param row Row to rename
   */
  onStartRename(row: ConductorRow): void {
    if (this.actionsDisabled) {
      return;
    }
    if (!this.closeActiveAction(() => this.onStartRename(row))) {
      return;
    }
    row.error = "";
    this.activeRow = row;
    this.activeKind = "editing";
    // Start from the RAW value: trimming it here would silently rewrite
    // historical names that keep trailing spaces
    this.draft = row.raw;
    this.draftError = "";
    this.applyFilter();
    this.focusRenameInput();
  }

  /**
   * Handle the inline rename field input
   *
   * @since 1.0.0
   *
   * @param value New draft value
   */
  onDraftChange(value: string): void {
    this.draft = typeof value === "string" ? value : "";
    this.draftError = "";
  }

  /**
   * Save the inline rename
   *
   * @since 1.0.0
   */
  onSaveRename(): void {
    if (
      this.contextError ||
      this.savingRow ||
      !this.activeRow ||
      this.activeKind !== "editing"
    ) {
      return;
    }

    const row = this.activeRow;
    // "from" raw and untouched, "to" normalized: see
    // DocumentArchivesService.renameConductor
    const from = row.raw;
    const to = this.draftNormalized;

    if (!to) {
      this.draftError = this.translateService.instant(
        I18N_ROOT + "errors.emptyName"
      );
      return;
    }
    if (to.length > CONDUCTOR_NAME_MAX_LENGTH) {
      this.draftError = this.translateService.instant(
        I18N_ROOT + "errors.tooLong",
        { max: CONDUCTOR_NAME_MAX_LENGTH }
      );
      return;
    }
    if (this.isUnchanged(to, from)) {
      this.draftError = this.translateService.instant(
        I18N_ROOT + "errors.sameName"
      );
      return;
    }
    if (this.isDuplicate(to, row)) {
      this.draftError = this.translateService.instant(
        I18N_ROOT + "errors.duplicate"
      );
      return;
    }

    this.draftError = "";
    this.savingRow = row;
    this.subscriptions.push(
      this.documentArchivesService
        .renameConductor(this.documentArchiveId, from, to)
        .pipe(finalize(() => (this.savingRow = null)))
        .subscribe(
          (res) => {
            if (!res || !res.status) {
              row.error = this.translateService.instant(
                I18N_ROOT + "errors.unexpected"
              );
              return;
            }
            const docsUpdated =
              res.data && typeof res.data.docsUpdated === "number"
                ? res.data.docsUpdated
                : DOCUMENT_COUNT_UNKNOWN;
            // Read before the rows get rebuilt: it tells whether the user was
            // expecting the cascade to touch something
            const expectedDocs = row.count;
            // Keep the count attached to the new name, otherwise the rebuilt
            // row falls back to "unknown" and re-enables the deletion
            this.moveCount(from, to, docsUpdated);
            this.renameLocal(from, to);
            this.changed = true;
            this.resetActiveAction();
            this.restoreFocus(to);
            this.toastrService.success(
              this.buildRenameSuccessMessage(res, docsUpdated, expectedDocs)
            );
          },
          (err) => this.handleOperationError(err, "rename", row)
        )
    );
  }

  /**
   * Ask for the inline deletion confirmation of the given row
   *
   * @since 1.0.0
   *
   * @param row Row to delete
   */
  onStartDelete(row: ConductorRow): void {
    if (this.actionsDisabled || !this.canDelete(row)) {
      return;
    }
    if (!this.closeActiveAction(() => this.onStartDelete(row))) {
      return;
    }
    row.error = "";
    this.activeRow = row;
    this.activeKind = "confirmingDelete";
    this.draft = "";
    this.draftError = "";
    this.applyFilter();
  }

  /**
   * Confirm the inline deletion
   *
   * @since 1.0.0
   */
  onConfirmDelete(): void {
    if (
      this.contextError ||
      this.savingRow ||
      !this.activeRow ||
      this.activeKind !== "confirmingDelete"
    ) {
      return;
    }

    const row = this.activeRow;
    this.savingRow = row;
    this.subscriptions.push(
      this.documentArchivesService
        .deleteConductor(this.documentArchiveId, row.raw)
        .pipe(finalize(() => (this.savingRow = null)))
        .subscribe(
          (res) => {
            if (!res || !res.status) {
              row.error = this.translateService.instant(
                I18N_ROOT + "errors.unexpected"
              );
              return;
            }
            this.forgetCount(row.raw);
            this.removeLocal(row.raw);
            this.changed = true;
            this.resetActiveAction();
            this.toastrService.success(
              this.resolveMessage(res, I18N_ROOT + "success.deleted")
            );
          },
          (err) => this.handleOperationError(err, "delete", row)
        )
    );
  }

  /**
   * Cancel the open inline action, asking for confirmation when the rename
   * draft holds unsaved changes
   *
   * @since 1.0.0
   */
  onCancelRowAction(): void {
    if (this.savingRow) {
      return;
    }
    const previousRaw = this.activeRow ? this.activeRow.raw : null;
    if (this.hasPendingDraft()) {
      this.confirmDiscardDraft(() => {
        this.resetActiveAction();
        this.restoreFocus(previousRaw);
      });
      return;
    }
    this.resetActiveAction();
    this.restoreFocus(previousRaw);
  }

  /**
   * Handle the ESC key: it cancels the open row action first and closes the
   * dialog only when every row is idle
   *
   * @since 1.0.0
   */
  onEscape(): void {
    if (this.savingRow || this.creating) {
      return;
    }
    if (this.activeRow) {
      this.onCancelRowAction();
      return;
    }
    this.attemptClose();
  }

  /**
   * Close the dialog, asking for confirmation when the rename draft holds
   * unsaved changes. The caller receives `true` when the archive changed and
   * must be re-fetched.
   *
   * @since 1.0.0
   */
  attemptClose(): void {
    if (this.savingRow || this.creating) {
      return;
    }
    if (this.hasPendingDraft()) {
      this.confirmDiscardDraft(() => this.dialogRef.close(this.changed));
      return;
    }
    this.dialogRef.close(this.changed);
  }

  /**
   * True when the given row can be deleted.
   *
   * A known count greater than zero means the back-end will certainly refuse
   * the deletion. An unknown or zero count still allows the attempt, because
   * deleted documents are not visible in the archive tree (the 409 is handled).
   *
   * @since 1.0.0
   *
   * @param row Row to check
   */
  canDelete(row: ConductorRow): boolean {
    if (this.contextError) {
      return false;
    }
    return row.count <= 0;
  }

  /**
   * Init/replace the managed list
   *
   * @since 1.0.0
   *
   * @param conductors Raw names coming from the back-end
   */
  private setConductors(conductors: any[]): void {
    const unique: string[] = [];
    conductors.forEach((item: any) => {
      const raw = typeof item === "string" ? item : String(item);
      // Duplicated raw values would be indistinguishable for every API call
      // (they are all name-based), so a single row is kept for each
      if (unique.indexOf(raw) === -1) {
        unique.push(raw);
      }
    });
    this.conductors = unique;
    this.buildRows();
  }

  /**
   * Rebuild the rows out of the raw list, sorted alphabetically with a stable
   * fallback on the back-end order
   *
   * @since 1.0.0
   */
  private buildRows(): void {
    const decorated = this.conductors.map((raw: string, index: number) => {
      return { raw, index };
    });
    // Array.prototype.sort is not guaranteed to be stable on ES5 targets:
    // the original index keeps ties in the back-end order
    decorated.sort((a, b) => {
      const compared = a.raw.trim().localeCompare(b.raw.trim());
      return compared !== 0 ? compared : a.index - b.index;
    });

    const placeholder = this.translateService.instant(I18N_ROOT + "unnamed");
    this.rows = decorated.map((item) => {
      const trimmed = item.raw.trim();
      return {
        raw: item.raw,
        display: trimmed ? trimmed : placeholder,
        blank: !trimmed,
        count: this.resolveCount(item.raw),
        error: "",
      };
    });
    this.applyFilter();
  }

  /**
   * Recompute the displayed rows out of the local search term. The row with
   * an open action is always kept visible.
   *
   * @since 1.0.0
   */
  private applyFilter(): void {
    const term = this.searchTerm ? this.searchTerm.trim().toLowerCase() : "";
    if (!term) {
      this.displayedRows = this.rows.slice();
      return;
    }
    this.displayedRows = this.rows.filter((row) => {
      return (
        row === this.activeRow || row.display.toLowerCase().indexOf(term) > -1
      );
    });
  }

  /**
   * Take an owned copy of the counts snapshot handed over by the caller
   *
   * @since 1.0.0
   *
   * @param source Map of counts keyed by conductor name
   */
  private copyDocumentCounts(source: any): void {
    this.documentCounts = Object.create(null);
    if (!source || typeof source !== "object") {
      return;
    }
    Object.keys(source).forEach((key: string) => {
      const value = source[key];
      if (typeof value === "number" && value >= 0) {
        this.documentCounts[key] = value;
      }
    });
  }

  /**
   * Resolve the document count of the given raw name
   *
   * @since 1.0.0
   *
   * @param raw Raw conductor name
   */
  private resolveCount(raw: string): number {
    // The tree groups documents by the value stored on the document, which
    // may differ from the archive entry by surrounding spaces only
    const candidates = [raw, raw.trim()];
    for (let i = 0; i < candidates.length; i++) {
      if (
        Object.prototype.hasOwnProperty.call(this.documentCounts, candidates[i])
      ) {
        const value = this.documentCounts[candidates[i]];
        if (typeof value === "number" && value >= 0) {
          return value;
        }
      }
    }
    return DOCUMENT_COUNT_UNKNOWN;
  }

  /**
   * Move the document count from the old name to the new one after a rename.
   *
   * `docsUpdated` is authoritative when the back-end returns it: it counts
   * every document the cascade touched, deleted ones included, while the
   * tree-derived count is only a lower bound. When it is missing, the previous
   * count is carried over so that a populated conductor keeps its deletion
   * blocked instead of silently falling back to "unknown".
   *
   * Only the exact `from` key is dropped: a sibling row whose trimmed value
   * happens to match the same tree key must keep its own count.
   *
   * @since 1.0.0
   *
   * @param from Previous raw name
   * @param to New name
   * @param docsUpdated Documents updated by the cascade, or
   *                    DOCUMENT_COUNT_UNKNOWN
   */
  private moveCount(from: string, to: string, docsUpdated: number): void {
    const previous = this.resolveCount(from);
    const next =
      docsUpdated !== DOCUMENT_COUNT_UNKNOWN ? docsUpdated : previous;

    if (Object.prototype.hasOwnProperty.call(this.documentCounts, from)) {
      delete this.documentCounts[from];
    }
    if (next !== DOCUMENT_COUNT_UNKNOWN) {
      this.documentCounts[to] = next;
    }
  }

  /**
   * Drop the document count of a name that no longer exists
   *
   * @since 1.0.0
   *
   * @param name Raw name to forget
   */
  private forgetCount(name: string): void {
    if (Object.prototype.hasOwnProperty.call(this.documentCounts, name)) {
      delete this.documentCounts[name];
    }
  }

  /**
   * Canonical form of a name typed by the user:
   * - every run of whitespace collapses into a single space and the edges are
   *   trimmed, so "primo     nome" becomes "primo nome";
   * - the result is uppercased, so "prova" is stored as "PROVA".
   *
   * This is the ONLY transform applied to an outgoing name, and it is applied
   * to the create payload and to the rename `to` - NEVER to the rename `from`,
   * which must stay byte-identical to the value stored in the archive.
   *
   * `toUpperCase()` and not `toLocaleUpperCase()`: the result gets persisted,
   * so it must not depend on the browser locale (a Turkish locale would turn
   * "i" into "İ").
   *
   * `\s` also covers tabs and the non-breaking spaces that come with a paste
   * from Word, and the input is capped at 200 chars, so the regex cannot be
   * fed an unbounded string. Note that uppercasing can still make the value
   * longer than what was typed ("ß" becomes "SS"): that is why the length
   * check runs on the normalized value, not on the raw one.
   *
   * @since 1.0.0
   *
   * @param value Raw value typed by the user
   */
  private normalizeName(value: string): string {
    if (typeof value !== "string") {
      return "";
    }
    return value
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase();
  }

  /**
   * Validator of the create field
   *
   * @since 1.0.0
   */
  private nameValidator = (control: AbstractControl): ValidationErrors => {
    const normalized = this.normalizeName(
      typeof control.value === "string" ? control.value : ""
    );
    if (!normalized) {
      return { conductorEmpty: true };
    }
    if (normalized.length > CONDUCTOR_NAME_MAX_LENGTH) {
      return { conductorTooLong: true };
    }
    if (this.isDuplicate(normalized, null)) {
      return { conductorDuplicate: true };
    }
    return null;
  };

  /**
   * Duplicate check against the managed list.
   *
   * Both sides are compared in their NORMALIZED form, which is uppercase by
   * construction, so the check is case-insensitive exactly like the back-end
   * one: "Mario Rossi", "mario rossi" and "mario rossi " all collide with an
   * existing "MARIO ROSSI" and are refused before any request leaves the
   * browser. Accepting them client-side would only cost a round trip and turn
   * a field error into a remote 409.
   *
   * Normalized values are compared directly instead of lowercasing them again:
   * the uppercase/lowercase round trip is not an identity for every character
   * ("ß" uppercases to "SS").
   *
   * Accents are significant, here as on the back-end: "CITTÀ" and "CITTA" are
   * two different conductors.
   *
   * @since 1.0.0
   *
   * @param candidate Normalized candidate name
   * @param exclude Row to skip (the one being renamed)
   */
  private isDuplicate(candidate: string, exclude: ConductorRow): boolean {
    return this.rows.some((row) => {
      if (exclude && row === exclude) {
        return false;
      }
      return this.normalizeName(row.raw) === candidate;
    });
  }

  /**
   * True when a rename would not actually change the stored name.
   *
   * Mirrors the back-end `conductor_name_unchanged` rule: the comparison
   * IGNORES CASE but RESPECTS SPACES. So renaming "mario rossi" to
   * "Mario Rossi" is refused (only the case would change, and the value is
   * uppercased on the way out anyway), while fixing a stray space -
   * "MARIO ROSSI " to "Mario Rossi" - is a real change and goes through.
   *
   * `from` is only uppercased for the comparison, never trimmed: the value
   * itself keeps travelling raw in the payload.
   *
   * @since 1.0.0
   *
   * @param to Normalized target name
   * @param from Raw current name
   */
  private isUnchanged(to: string, from: string): boolean {
    return to === from.toUpperCase();
  }

  /**
   * True when the open rename holds unsaved changes
   *
   * @since 1.0.0
   */
  private hasPendingDraft(): boolean {
    return (
      this.activeKind === "editing" &&
      this.activeRow !== null &&
      this.draft !== this.activeRow.raw
    );
  }

  /**
   * Close the open row action before opening another one.
   *
   * Returns true when the caller can proceed synchronously, false when a
   * discard confirmation has been opened (`onDone` runs once confirmed).
   *
   * @since 1.0.0
   *
   * @param onDone Callback to run after a confirmed discard
   */
  private closeActiveAction(onDone: () => void): boolean {
    if (!this.activeRow) {
      return true;
    }
    if (!this.hasPendingDraft()) {
      this.resetActiveAction();
      return true;
    }
    this.confirmDiscardDraft(() => {
      this.resetActiveAction();
      onDone();
    });
    return false;
  }

  /**
   * Ask the user whether the pending rename draft can be discarded
   *
   * @since 1.0.0
   *
   * @param onConfirm Callback to run when the user confirms
   */
  private confirmDiscardDraft(onConfirm: () => void): void {
    const dialogRef = this.confirmDialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(I18N_ROOT + "discard.title"),
        message: this.translateService.instant(I18N_ROOT + "discard.message"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    this.subscriptions.push(
      dialogRef
        .beforeClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (res) {
            onConfirm();
          }
        })
    );
  }

  /**
   * Reset the open row action
   *
   * @since 1.0.0
   */
  private resetActiveAction(): void {
    this.activeRow = null;
    this.activeKind = null;
    this.draft = "";
    this.draftError = "";
    this.applyFilter();
  }

  /**
   * Add the given name to the local list
   *
   * @since 1.0.0
   *
   * @param name Persisted name
   */
  private addLocal(name: string): void {
    if (this.conductors.indexOf(name) === -1) {
      this.conductors.push(name);
    }
    this.buildRows();
  }

  /**
   * Replace `from` with `to` in the local list
   *
   * @since 1.0.0
   *
   * @param from Previous raw name
   * @param to New name
   */
  private renameLocal(from: string, to: string): void {
    const index = this.conductors.indexOf(from);
    if (index > -1) {
      this.conductors[index] = to;
    } else if (this.conductors.indexOf(to) === -1) {
      this.conductors.push(to);
    }
    this.buildRows();
  }

  /**
   * Remove the given name from the local list
   *
   * @since 1.0.0
   *
   * @param name Raw name to remove
   */
  private removeLocal(name: string): void {
    const index = this.conductors.indexOf(name);
    if (index > -1) {
      this.conductors.splice(index, 1);
    }
    this.buildRows();
  }

  /**
   * Re-fetch the archive to realign the local list after a concurrent change.
   *
   * `preserveRaw`/`preserveMessage` re-attach an inline error to the reloaded
   * row, so that the reason of the failure is not lost by the refresh.
   *
   * @since 1.0.0
   *
   * @param [preserveRaw] Raw name whose inline error must survive the reload
   * @param [preserveMessage] Inline error message to re-attach
   */
  private reloadConductors(
    preserveRaw?: string,
    preserveMessage?: string
  ): void {
    // The archive changed under us: the caller must re-fetch it as well
    this.changed = true;
    if (!this.documentArchiveId) {
      return;
    }
    // The caller's reader when it handed one over (the 3.0.0 dossier),
    // otherwise the legacy archive, as always
    const source: Observable<ApiResponse<DocumentArchiveNameLists>> =
      this.data && this.data.reloadNames
        ? this.data.reloadNames()
        : this.documentArchivesService.get(this.documentArchiveId);
    this.subscriptions.push(
      source.subscribe(
        (res) => {
          if (
            !res ||
            !res.status ||
            !res.data ||
            !Array.isArray(res.data.conductorName)
          ) {
            return;
          }
          this.resetActiveAction();
          this.setConductors(res.data.conductorName);
          this.createControl.updateValueAndValidity();
          if (typeof preserveRaw === "string") {
            const target = this.rows.filter((row) => row.raw === preserveRaw)[0];
            if (target) {
              target.error = preserveMessage;
            }
          }
        },
        () => {
          // Either reader already notifies the user (@see ConductorManagementDialogData.reloadNames)
        }
      )
    );
  }

  /**
   * Map an API failure to a user-facing message and place it where it belongs.
   *
   * The back-end `message` is deliberately ignored: it is already translated
   * server-side following the SERVER configuration, not the request language.
   * Branching happens on status code + operation only.
   *
   * @since 1.0.0
   *
   * @param err Error response
   * @param operation "create" | "rename" | "delete"
   * @param row Row the operation belongs to (null on create)
   */
  private handleOperationError(
    err: any,
    operation: string,
    row: ConductorRow
  ): void {
    const status = err && typeof err.status === "number" ? err.status : 0;

    // The session is gone (ApiService already retried with the refresh token
    // before surfacing this): nothing can be done from inside the dialog.
    if (status === 401) {
      this.contextError = this.translateService.instant(
        I18N_ROOT + "errors.notAuthenticated"
      );
      this.resetActiveAction();
      return;
    }

    // Two distinct back-end checks answer 403: the role lacks
    // canCreateDocuments, or its type is not admin/operator. Neither is
    // retriable, and neither can be told apart from the response. Since the
    // entry point is already gated on both, in practice it shows up when the
    // active role changed underneath.
    if (status === 403) {
      this.contextError = this.translateService.instant(
        I18N_ROOT + "errors.forbidden"
      );
      this.resetActiveAction();
      return;
    }

    // On create and delete a 404 means the archive or the property does not
    // exist: nothing can be retried. On rename it is ambiguous, because it also
    // covers `conductor_not_found` (a `from` that is neither in the list nor on
    // any document), so there the list is reloaded instead of locking the
    // dialog. Delete is idempotent on an unknown name, so it never 404s for
    // that reason.
    if (status === 404 && operation !== "rename") {
      this.contextError = this.translateService.instant(
        I18N_ROOT + "errors.archiveNotFound"
      );
      this.resetActiveAction();
      return;
    }

    let message = "";
    let reload = false;
    if (status === 404) {
      message = this.translateService.instant(
        I18N_ROOT + "errors.renameSourceNotFound"
      );
      reload = true;
    } else if (status === 409) {
      if (operation === "delete") {
        message = this.translateService.instant(
          I18N_ROOT + "errors.deleteHasDocuments"
        );
      } else {
        // Somebody else created that name in the meantime
        message = this.translateService.instant(
          I18N_ROOT + "errors.duplicateRemote"
        );
        reload = true;
      }
    } else if (status === 400) {
      // Three back-end rules answer 400 without field errors:
      // conductor_name_required, conductor_name_too_long and (rename only)
      // conductor_name_unchanged. The first two are already blocked
      // client-side, so on a rename the remaining plausible cause is the
      // case-insensitive "unchanged" rule.
      let key = "errors.rejectedName";
      if (this.hasFieldErrors(err)) {
        key = "errors.invalidName";
      } else if (operation === "rename") {
        key = "errors.sameName";
      }
      message = this.translateService.instant(I18N_ROOT + key);
    } else {
      message = this.translateService.instant(
        I18N_ROOT + "errors.unexpected"
      );
    }

    if (operation === "create") {
      this.createServerError = message;
      if (reload) {
        this.reloadConductors();
      }
      return;
    }

    if (row) {
      row.error = message;
    }
    if (reload) {
      this.reloadConductors(row ? row.raw : undefined, message);
    }
  }

  /**
   * True when the error payload carries field validation errors
   *
   * @since 1.0.0
   *
   * @param err Error response
   */
  private hasFieldErrors(err: any): boolean {
    return !!(
      err &&
      err.error &&
      err.error.errors &&
      typeof err.error.errors === "object"
    );
  }

  /**
   * Build the rename success message, exposing how many documents the cascade
   * actually updated
   *
   * @since 1.0.0
   *
   * @param res API response
   * @param docsUpdated Amount of updated documents, or DOCUMENT_COUNT_UNKNOWN
   * @param expectedDocs Amount of documents the row was showing before the
   *                     rename, or DOCUMENT_COUNT_UNKNOWN
   */
  private buildRenameSuccessMessage(
    res: any,
    docsUpdated: number,
    expectedDocs: number
  ): string {
    if (docsUpdated === DOCUMENT_COUNT_UNKNOWN) {
      return this.resolveMessage(res, I18N_ROOT + "success.renamed");
    }
    if (docsUpdated === 0) {
      // A zero cascade is only worth reporting when the row WAS showing
      // documents: it means the name stored on them is not byte-identical to
      // the one in the archive list, so the rename did not reach them.
      //
      // An unknown count must NOT raise the warning: the count is derived from
      // the archive tree, which only holds groups with at least one document,
      // so every empty conductor (a freshly created one, typically) is
      // unknown and renaming it legitimately updates nothing.
      if (expectedDocs > 0) {
        return this.translateService.instant(
          I18N_ROOT + "success.renamedNoDocs"
        );
      }
      return this.resolveMessage(res, I18N_ROOT + "success.renamed");
    }
    return this.translateService.instant(I18N_ROOT + "success.renamedWithDocs", {
      count: docsUpdated,
    });
  }

  /**
   * Use the back-end message when available, fall back on a local key
   *
   * @since 1.0.0
   *
   * @param res API response
   * @param fallbackKey i18n key used when the response carries no message
   */
  private resolveMessage(res: any, fallbackKey: string): string {
    if (res && typeof res.message === "string" && res.message) {
      return res.message;
    }
    return this.translateService.instant(fallbackKey);
  }

  /**
   * Move the focus into the inline rename field
   *
   * @since 1.0.0
   */
  private focusRenameInput(): void {
    setTimeout(() => {
      if (this.renameInputs && this.renameInputs.first) {
        this.renameInputs.first.nativeElement.focus();
      }
    }, 0);
  }

  /**
   * Give the focus back to the rename button of the given row, once every row
   * is rendered in its idle state again
   *
   * @since 1.0.0
   *
   * @param raw Raw name of the row that owned the closed action
   */
  private restoreFocus(raw: string): void {
    if (typeof raw !== "string") {
      return;
    }
    setTimeout(() => {
      const index = this.displayedRows
        .map((row) => row.raw)
        .indexOf(raw);
      const triggers = this.renameTriggers ? this.renameTriggers.toArray() : [];
      if (index > -1 && triggers[index]) {
        triggers[index].nativeElement.focus();
      }
    }, 0);
  }
}
